import { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useMutation, useAction, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  addSession,
  updateSession,
  setSessionStatus,
  clearSteps,
  addStep,
  addSkillPrompt,
  removeSkillPrompt,
  updateSkillPrompt,
  brandId,
  PromptSessionStatus,
  TimelineStepKind,
  type PromptSession,
  type PromptSessionId,
  type SkillPrompt,
  type TimelineStep,
  type TimelineStepId,
} from '@repancy/core';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { ModelSelector } from './ModelSelector';
import {
  SpecializationSelector,
  type SpecializationSelection,
} from './SpecializationSelector';
import { SystemPromptEditor } from './SystemPromptEditor';
import { UserPromptEditor } from './UserPromptEditor';
import { SkillPromptSlots } from './SkillPromptSlots';
import { StreamingOutput } from './StreamingOutput';
import { DocumentContext } from './DocumentContext';
import { TimelinePanel } from './TimelinePanel';
import { HelpTip } from '../ui/HelpTip';

const FONT = "var(--xp-font-body)";

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: '4px 2px',
  borderBottom: '1px solid #808080',
  marginBottom: 8,
  flexWrap: 'wrap',
};

const toolBtnStyle: React.CSSProperties = {
  fontFamily: FONT,
  fontSize: 11,
  padding: '3px 10px',
  background: '#ece9d8',
  color: '#000',
  borderStyle: 'solid',
  cursor: 'pointer',
};

const toolBtnDisabledStyle: React.CSSProperties = {
  ...toolBtnStyle,
  color: '#808080',
  cursor: 'default',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'stretch',
  marginBottom: 8,
};

const splitStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

const leftPaneStyle: React.CSSProperties = {
  flex: '0 0 45%',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  minWidth: 0,
};

const rightPaneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const statusBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  padding: '4px 6px',
  borderTop: '1px solid #808080',
  marginTop: 8,
  fontFamily: FONT,
  fontSize: 10,
  color: '#000',
};

const statusCellStyle: React.CSSProperties = {
  borderStyle: 'solid',
  borderWidth: 1,
  padding: '1px 8px',
  background: '#ece9d8',
};

function createDefaultSession(): PromptSession {
  return {
    id: brandId<'PromptSession'>(`session-${Date.now()}`) as PromptSessionId,
    systemPrompt: '',
    userPrompt: '',
    skillPrompts: [],
    modelId: 'anthropic-sonnet-4',
    documentContextIds: [],
    status: PromptSessionStatus.Idle,
    createdAt: new Date().toISOString(),
    userId: '',
  };
}

export function PromptLabWindow() {
  const dispatch = useAppDispatch();
  const sessionIds = useAppSelector((s) => s.promptSessions.sessions.ids);
  const sessionIdRef = useRef<string | null>(null);

  // F-NIPE-PROMPT-LAB-SKILLS — subscribe to the firm's Skills library
  // so the driver bridge can resolve skill names → canonical rows.
  // Mirrors what SkillPicker does. Outside a portal (no slug) the
  // subscription is skipped and library-mode falls back to an error.
  //
  // 2026-05-28 — useResolvedPortalSlug combines the React context
  // with a URL fallback so this works even when the window is
  // rendered by the OUTER WindowLayer (outside
  // TenantBrandingProvider).
  const promptLabPortalSlug = useResolvedPortalSlug();
  const skillLibraryForBridge = useQuery(
    api.skills.listForSlug,
    promptLabPortalSlug !== null
      ? { clientSlug: promptLabPortalSlug }
      : 'skip',
  );
  // L.11 + D2 — Specialization selection. When set, Run routes the
  // prompt through `inferenceJobs.request({loraUrl})` instead of the
  // base-only placeholder. The selector lifts the resolved row up so
  // we don't keep a second lorasByPortal subscription here.
  const [selectedSpecialization, setSelectedSpecialization] =
    useState<SpecializationSelection | null>(null);

  // D2 — in-flight inference job state.
  type JobState =
    | { kind: 'idle' }
    | { kind: 'queued'; jobId: string }
    | { kind: 'error'; message: string };
  const [jobState, setJobState] = useState<JobState>({ kind: 'idle' });

  // F-NIPE-DESKTOP-AWARENESS Phase 2 — read-only bridge for
  // readWindowContext('/prompt-lab'). NIPE reads which session is
  // active + whether a specialization is wired in so it can ground
  // suggestions in what the user has loaded.
  const promptLabBridgeRef = useRef({
    sessionIds,
    jobState,
    selectedSpecialization,
  });
  promptLabBridgeRef.current = { sessionIds, jobState, selectedSpecialization };
  useEffect(() => {
    window.__repancyPromptLab = () => {
      const cur = promptLabBridgeRef.current;
      return {
        activeSessionId: sessionIdRef.current,
        sessionCount: cur.sessionIds.length,
        selectedSpecializationName:
          (cur.selectedSpecialization as { name?: string } | null)?.name ?? null,
        jobStatus: cur.jobState.kind,
        jobErrorMessage:
          cur.jobState.kind === 'error' ? cur.jobState.message : null,
      };
    };
    return () => {
      delete window.__repancyPromptLab;
    };
  }, []);

  const requestInference = useMutation(api.inferenceJobs.request);
  const kickSampler = useAction(api.inferenceJobs.kickSampler);

  // Create default session on mount if none exists
  useEffect(() => {
    if (sessionIds.length === 0) {
      const session = createDefaultSession();
      sessionIdRef.current = session.id;
      dispatch(addSession(session));
    } else if (!sessionIdRef.current) {
      sessionIdRef.current = sessionIds[0] ?? null;
    }
  }, [dispatch, sessionIds]);

  const activeSessionId = sessionIdRef.current ?? sessionIds[0] ?? null;

  const session = useAppSelector((s) =>
    activeSessionId ? s.promptSessions.sessions.entities[activeSessionId] : null,
  );

  // Hoisted above first use (handleSaveOutput) so the const isn't in
  // the temporal dead zone when the callback closes over it. Safe-access
  // via optional chaining handles the brief render where `session` is
  // null before the bootstrap useEffect creates the default session.
  const isRunning = session?.status === PromptSessionStatus.Running;

  // F-NIPE-PROMPT-LAB-DRIVER — read/mutate bridge. Distinct from the
  // metadata-only `__repancyPromptLab` registered above. Kept on a
  // ref so dispatch closes over the freshest activeSessionId + session
  // body each call without re-registering on every keystroke.
  const driverRef = useRef({
    activeSessionId,
    session,
    selectedSpecialization,
    skillLibraryForBridge,
  });
  driverRef.current = {
    activeSessionId,
    session,
    selectedSpecialization,
    skillLibraryForBridge,
  };
  useEffect(() => {
    // F-NIPE-PROMPT-LAB-SKILLS — unique-name guard. Skill prompt names
    // must be case-insensitive unique within a session so the user
    // (and the model on re-read) can address them unambiguously.
    // Returns the conflicting existing skillPrompt or null.
    const findNameConflict = (
      proposedName: string,
      excludeSkillPromptId?: string,
    ): { id: string; name: string } | null => {
      const cur = driverRef.current;
      if (!cur.session) return null;
      const needle = proposedName.trim().toLowerCase();
      if (needle.length === 0) return null;
      for (const sp of cur.session.skillPrompts) {
        if (excludeSkillPromptId && sp.id === excludeSkillPromptId) continue;
        if (sp.name.trim().toLowerCase() === needle) {
          return { id: sp.id, name: sp.name };
        }
      }
      return null;
    };
    const nextSkillPromptId = (): string =>
      `sp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    window.__repancyPromptLabDriver = {
      getActiveSessionId: () => driverRef.current.activeSessionId,
      readActiveSession: () => {
        const cur = driverRef.current;
        if (!cur.activeSessionId || !cur.session) return null;
        return {
          sessionId: cur.activeSessionId,
          systemPrompt: cur.session.systemPrompt,
          userPrompt: cur.session.userPrompt,
          skillPrompts: cur.session.skillPrompts.map((sp) => ({
            id: sp.id,
            name: sp.name,
            content: sp.content,
            weight: sp.weight,
            enabled: sp.enabled,
          })),
          modelId: cur.session.modelId,
          status: String(cur.session.status),
          selectedSpecializationName:
            (cur.selectedSpecialization as { name?: string } | null)?.name ?? null,
        };
      },
      setSystemPrompt: (sessionId, text) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        dispatch(updateSession({ id: sessionId, changes: { systemPrompt: text } }));
        return { ok: true };
      },
      setUserPrompt: (sessionId, text) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        dispatch(updateSession({ id: sessionId, changes: { userPrompt: text } }));
        return { ok: true };
      },
      addLibrarySkill: (sessionId, skillName, weight, enabled) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        const library = driverRef.current.skillLibraryForBridge;
        if (!library) {
          return {
            ok: false,
            reason:
              'Skill library is not loaded (no portal slug or query still in flight).',
          };
        }
        const needle = skillName.trim().toLowerCase();
        const row = library.find((s) => s.name.toLowerCase() === needle);
        if (!row) {
          return {
            ok: false,
            reason: `No library skill named "${skillName}". Call listSkills to see options.`,
          };
        }
        const conflict = findNameConflict(row.name);
        if (conflict) {
          return {
            ok: false,
            reason: `A skill named "${conflict.name}" is already attached to this session (id ${conflict.id}). Remove it first or update the existing slot instead.`,
          };
        }
        const skillPrompt: SkillPrompt = {
          id: nextSkillPromptId(),
          name: row.name,
          content: row.description,
          weight,
          enabled,
          skillId: String(row._id),
          skillVersionNumber: row.currentVersion,
        };
        dispatch(addSkillPrompt({ sessionId, skillPrompt }));
        return { ok: true, skillPromptId: skillPrompt.id };
      },
      addCustomSkill: (sessionId, name, content, weight, enabled) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        const conflict = findNameConflict(name);
        if (conflict) {
          return {
            ok: false,
            reason: `A skill named "${conflict.name}" is already attached to this session (id ${conflict.id}). Pick a different name or update the existing slot.`,
          };
        }
        const skillPrompt: SkillPrompt = {
          id: nextSkillPromptId(),
          name,
          content,
          weight,
          enabled,
        };
        dispatch(addSkillPrompt({ sessionId, skillPrompt }));
        return { ok: true, skillPromptId: skillPrompt.id };
      },
      removeSkill: (sessionId, skillPromptId) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        const cur = driverRef.current.session;
        if (!cur || !cur.skillPrompts.find((sp) => sp.id === skillPromptId)) {
          return {
            ok: false,
            reason: `No skill prompt with id "${skillPromptId}" on the active session.`,
          };
        }
        dispatch(removeSkillPrompt({ sessionId, skillPromptId }));
        return { ok: true };
      },
      updateSkill: (sessionId, skillPromptId, changes) => {
        if (driverRef.current.activeSessionId !== sessionId) {
          return { ok: false, reason: 'sessionId mismatches the active session.' };
        }
        const cur = driverRef.current.session;
        if (!cur || !cur.skillPrompts.find((sp) => sp.id === skillPromptId)) {
          return {
            ok: false,
            reason: `No skill prompt with id "${skillPromptId}" on the active session.`,
          };
        }
        // If renaming, enforce unique-name across the OTHER slots.
        if (typeof changes.name === 'string') {
          const conflict = findNameConflict(changes.name, skillPromptId);
          if (conflict) {
            return {
              ok: false,
              reason: `Another skill named "${conflict.name}" is already attached (id ${conflict.id}). Pick a different name.`,
            };
          }
        }
        dispatch(updateSkillPrompt({ sessionId, skillPromptId, changes }));
        return { ok: true };
      },
    };
    return () => {
      delete window.__repancyPromptLabDriver;
    };
  }, [dispatch]);

  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);
  const steps = useMemo(() => {
    if (!activeSessionId) return [];
    return stepIds
      .map((id) => stepEntities[id])
      .filter((st) => st && st.sessionId === activeSessionId);
  }, [activeSessionId, stepIds, stepEntities]);

  const totalTokens = steps.reduce((sum, st) => sum + (st ? st.endToken - st.startToken : 0), 0);

  // D2 — append a finished output to the timeline as a step.
  const appendStep = useCallback(
    (label: string, content: string) => {
      if (!activeSessionId) return;
      const stepId = brandId<'TimelineStep'>(
        `step-${activeSessionId}-${Date.now()}`,
      ) as TimelineStepId;
      const tokens = Math.max(1, Math.round(content.length / 4));
      const step: TimelineStep = {
        id: stepId,
        sessionId: activeSessionId as PromptSessionId,
        kind: TimelineStepKind.TextOutput,
        label,
        content,
        startToken: totalTokens,
        endToken: totalTokens + tokens,
        timestamp: new Date().toISOString(),
      };
      dispatch(addStep(step));
    },
    [dispatch, activeSessionId, totalTokens],
  );

  const handleRun = useCallback(async () => {
    if (!activeSessionId || !session) return;
    const prompt = session.userPrompt.trim();
    if (prompt.length === 0) return;

    // Base-only path (no specialization selected) — keep the
    // placeholder behavior so the StreamingOutput stub still works.
    if (selectedSpecialization === null) {
      dispatch(
        setSessionStatus({
          id: activeSessionId,
          status: PromptSessionStatus.Running,
        }),
      );
      return;
    }

    // Specialization-selected path — real inferenceJobs round-trip.
    dispatch(
      setSessionStatus({
        id: activeSessionId,
        status: PromptSessionStatus.Running,
      }),
    );
    try {
      const jobId = await requestInference({
        prompt,
        baseModel: selectedSpecialization.baseModel,
        loraUrl: selectedSpecialization.tinkerLoraUrl,
      });
      setJobState({ kind: 'queued', jobId });
      const result = await kickSampler({
        inferenceJobId: jobId,
      });
      if (!result.ok) {
        setJobState({
          kind: 'error',
          message:
            result.reason ?? 'Worker unreachable. See server logs.',
        });
        dispatch(
          setSessionStatus({
            id: activeSessionId,
            status: PromptSessionStatus.Idle,
          }),
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setJobState({ kind: 'error', message });
      dispatch(
        setSessionStatus({
          id: activeSessionId,
          status: PromptSessionStatus.Idle,
        }),
      );
    }
  }, [
    activeSessionId,
    session,
    selectedSpecialization,
    requestInference,
    kickSampler,
    dispatch,
  ]);

  const handleStop = useCallback(() => {
    if (!activeSessionId) return;
    // For the base-only placeholder path we still want to flush the
    // current prompt as a step so the Timeline panel has something to
    // scrub. For the live inference path the poll effect appends the
    // completed text; Stop simply clears local state.
    if (selectedSpecialization === null && session) {
      const content =
        session.userPrompt.trim().length > 0
          ? session.userPrompt
          : '(empty prompt)';
      appendStep(`Run ${steps.length + 1}`, content);
    }
    setJobState({ kind: 'idle' });
    dispatch(
      setSessionStatus({
        id: activeSessionId,
        status: PromptSessionStatus.Idle,
      }),
    );
  }, [
    activeSessionId,
    session,
    selectedSpecialization,
    steps.length,
    appendStep,
    dispatch,
  ]);

  // D2 — poll the in-flight inference job and surface result on completion.
  const pollJobId = jobState.kind === 'queued' ? jobState.jobId : null;
  const pollResult = useQuery(
    api.inferenceJobs.get,
    pollJobId
      ? { jobId: pollJobId }
      : 'skip',
  ) as
    | { status: string; text?: string; errorMessage?: string }
    | null
    | undefined;

  useEffect(() => {
    if (jobState.kind !== 'queued') return;
    if (!pollResult) return;
    if (pollResult.status === 'complete' && pollResult.text) {
      const label = selectedSpecialization
        ? `Specialized (${selectedSpecialization.baseModel} · rank ${selectedSpecialization.loraRank})`
        : 'Specialized run';
      appendStep(label, pollResult.text);
      setJobState({ kind: 'idle' });
      if (activeSessionId) {
        dispatch(
          setSessionStatus({
            id: activeSessionId,
            status: PromptSessionStatus.Idle,
          }),
        );
      }
    } else if (pollResult.status === 'failed') {
      setJobState({
        kind: 'error',
        message: pollResult.errorMessage ?? 'Inference failed',
      });
      if (activeSessionId) {
        dispatch(
          setSessionStatus({
            id: activeSessionId,
            status: PromptSessionStatus.Idle,
          }),
        );
      }
    }
  }, [
    pollResult,
    jobState,
    selectedSpecialization,
    appendStep,
    activeSessionId,
    dispatch,
  ]);

  const handleNewSession = useCallback(() => {
    const session = createDefaultSession();
    sessionIdRef.current = session.id;
    dispatch(addSession(session));
  }, [dispatch]);

  const handleClear = useCallback(() => {
    dispatch(clearSteps());
    if (activeSessionId) {
      dispatch(
        updateSession({
          id: activeSessionId,
          changes: { systemPrompt: '', userPrompt: '' },
        }),
      );
    }
  }, [dispatch, activeSessionId]);

  // Phase-J dead-button sweep — Save Output downloads the current
  // timeline (system prompt + user prompt + each step's content) as a
  // markdown file. No Convex write; the user keeps a local copy. The
  // button stays disabled while the session is running (matches the
  // existing visual state).
  const handleSaveOutput = useCallback(() => {
    if (!session || isRunning) return;
    const lines: string[] = [];
    lines.push(`# Prompt Lab Output`);
    lines.push('');
    lines.push(`- Session: ${session.id}`);
    lines.push(`- Model: ${session.modelId}`);
    lines.push(`- Created: ${session.createdAt}`);
    lines.push(`- Total tokens: ${totalTokens}`);
    lines.push('');
    if (session.systemPrompt) {
      lines.push(`## System Prompt`);
      lines.push('');
      lines.push(session.systemPrompt);
      lines.push('');
    }
    if (session.userPrompt) {
      lines.push(`## User Prompt`);
      lines.push('');
      lines.push(session.userPrompt);
      lines.push('');
    }
    if (steps.length > 0) {
      lines.push(`## Timeline`);
      lines.push('');
      for (const step of steps) {
        if (!step) continue;
        lines.push(`### ${step.kind} — step ${step.id}`);
        lines.push('');
        lines.push('```');
        lines.push(step.content ?? '');
        lines.push('```');
        lines.push('');
      }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt-lab-${session.id}-${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [session, isRunning, steps, totalTokens]);

  const handleModelSelect = useCallback(
    (modelId: string) => {
      if (!activeSessionId) return;
      dispatch(updateSession({ id: activeSessionId, changes: { modelId } }));
    },
    [dispatch, activeSessionId],
  );

  if (!activeSessionId || !session) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          fontFamily: FONT,
          fontSize: 12,
          color: '#808080',
        }}
      >
        Initializing session...
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        fontFamily: FONT,
      }}
      data-testid={testIds.promptLab.root}
    >
      {/* Toolbar */}
      <div style={toolbarStyle} data-testid={testIds.promptLab.toolbar}>
        <button
          style={toolBtnStyle}
          onClick={handleNewSession}
          title="Start a fresh prompt session. Steps and forks from the current session stay in the Timeline panel below."
          data-testid={testIds.promptLab.newSession}
        >
          New Session
        </button>
        <button
          style={toolBtnStyle}
          onClick={handleClear}
          title="Clear the system + user prompt for the active session and reset the step timeline."
          data-testid={testIds.promptLab.clearSession}
        >
          Clear
        </button>
        <button
          style={isRunning ? toolBtnDisabledStyle : toolBtnStyle}
          onClick={handleSaveOutput}
          disabled={isRunning}
          title="Download the current session (prompts + steps) as a markdown file."
          data-testid={testIds.promptLab.saveOutput}
        >
          Save Output
        </button>
      </div>

      {/* Top bar: model selector + specialization + run/stop. On mobile,
          let the bar wrap so Run + Stop don't get pushed off the right
          edge by the dropdowns. flex-wrap is additive over the inline
          `display: flex` from topBarStyle (which only sets display + gap
          + alignItems + marginBottom). */}
      <div className="max-md:flex-wrap" style={topBarStyle}>
        <HelpTip k="promptLab.modelSelector" wrap>
          <span>
            <ModelSelector
              selectedModelId={session.modelId}
              onSelect={handleModelSelect}
            />
          </span>
        </HelpTip>
        <HelpTip k="promptLab.specializationSelector" wrap>
          <span>
            <SpecializationSelector
              selectedLoraRowId={selectedSpecialization?.loraRowId ?? null}
              onSelect={setSelectedSpecialization}
            />
          </span>
        </HelpTip>
        <HelpTip k="promptLab.runButton" wrap>
          <button
            style={{
              ...toolBtnStyle,
              background: isRunning ? '#808080' : '#008000',
              color: '#fff',
              fontWeight: 'bold',
              padding: '3px 16px',
            }}
            onClick={handleRun}
            disabled={isRunning}
            data-testid={testIds.promptLab.runButton}
          >
            Run
          </button>
        </HelpTip>
        <HelpTip k="promptLab.stopButton" wrap>
          <button
            style={{
              ...toolBtnStyle,
              background: isRunning ? '#800000' : '#808080',
              color: '#fff',
              fontWeight: 'bold',
              padding: '3px 16px',
            }}
            onClick={handleStop}
            disabled={!isRunning}
            data-testid={testIds.promptLab.stopButton}
          >
            Stop
          </button>
        </HelpTip>
      </div>

      {selectedSpecialization !== null && (
        <div
          data-testid={testIds.promptLab.specializationBanner}
          style={{
            padding: '4px 6px',
            background: '#fffbcc',
            borderTop: '1px solid #b08800',
            borderBottom: '1px solid #b08800',
            fontFamily: 'var(--xp-font-body)',
            fontSize: 11,
            color: '#000',
          }}
        >
          Specialization active —{' '}
          <strong>{selectedSpecialization.baseModel}</strong> · rank{' '}
          {selectedSpecialization.loraRank}. Run routes through{' '}
          <code>inferenceJobs.request</code> with this LoRA. For
          side-by-side base vs specialized output use the{' '}
          <strong>Specialization Lab</strong>.
        </div>
      )}
      {jobState.kind === 'queued' && (
        <div
          data-testid={testIds.promptLab.inferenceStatus}
          style={{
            padding: '4px 6px',
            background: '#eef',
            borderBottom: '1px solid #88a',
            fontFamily: 'var(--xp-font-body)',
            fontSize: 11,
            color: '#000',
          }}
        >
          Sampling job <code>{jobState.jobId.slice(0, 12)}…</code> —
          ACA worker dispatched; polling for result.
        </div>
      )}
      {jobState.kind === 'error' && (
        <div
          data-testid={testIds.promptLab.inferenceError}
          style={{
            padding: '4px 6px',
            background: '#fee',
            borderBottom: '1px solid #a44',
            fontFamily: 'var(--xp-font-body)',
            fontSize: 11,
            color: '#400',
          }}
        >
          Inference failed: {jobState.message}
        </div>
      )}

      {/* Split pane — stacks vertically on mobile so each pane gets the
          full viewport width (editors + output are both unusable when each
          gets ~145px of a 320px screen). md: keeps the existing 45/55
          row split unchanged. The inline styles set `display: flex` only,
          which max-md:flex-col reliably overrides. */}
      <div className="max-md:flex-col" style={splitStyle}>
        {/* Left: editors — flex-1 on mobile gives the editors all the
            width; the inline `flex: '0 0 45%'` from leftPaneStyle still
            wins on md+ (Tailwind class only applies max-md). */}
        <div className="max-md:!flex-auto max-md:!w-full" style={leftPaneStyle}>
          {/* Document chips first so the lawyer immediately sees which
              documents are attached to the session. */}
          <DocumentContext sessionId={activeSessionId} />
          <SystemPromptEditor sessionId={activeSessionId} />
          <SkillPromptSlots sessionId={activeSessionId} />
          <UserPromptEditor sessionId={activeSessionId} onRun={handleRun} />
        </div>

        {/* Right: output */}
        <div className="max-md:!w-full max-md:min-h-[40vh]" style={rightPaneStyle}>
          <StreamingOutput sessionId={activeSessionId} />
        </div>
      </div>

      {/* Embedded Timeline panel (collapsible). Replaces the retired
          standalone /timeline window. */}
      <TimelinePanel sessionId={activeSessionId} />

      {/* Status bar — wraps on mobile so the Model cell doesn't overflow. */}
      <div className="max-md:flex-wrap max-md:gap-y-1" style={statusBarStyle} data-testid={testIds.promptLab.statusBar}>
        <span style={statusCellStyle}>
          Tokens: {totalTokens.toLocaleString()}
        </span>
        <span style={statusCellStyle}>
          Cost: ${(totalTokens * 0.003 / 1000).toFixed(4)}
        </span>
        <span style={statusCellStyle}>
          Status: {session.status}
        </span>
        <span style={statusCellStyle}>
          Model: {session.modelId}
        </span>
        <span style={{ flex: 1 }} />
        <span style={statusCellStyle}>
          Steps: {steps.length}
        </span>
      </div>
    </div>
  );
}
